import React, {
  KeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import classNames from 'classnames';
import { trackEvent } from '@gjensidige/core-analytics/lib/track-event';
import { TrackingContext } from '@gjensidige/core-analytics/lib/tracking-context';
import { TrackEventProps } from '@gjensidige/core-analytics/lib/types';
import { Close } from '@gjensidige/core-icons/lib/close';
import { useEscape, useOnTouchOutside } from '@gjensidige/core-utils';

import { DEFAULT_TRACKING } from './tracking';

import styles from '../../less/modal.module.less';

export interface Props {
  /** Use to force open state */
  visible: boolean;
  /** Content of the modal */
  children?: React.ReactNode;
  /** The aria label for the modal. Should be implemented in order for the modal to have a label readable by screen readers. */
  ariaLabel: string;
  /** The aria label for the close button. Use the same language as specied in the page. */
  ariaLabelCloseButton: string;
}

export interface DefaultProps {
  /** Defaults to false. If true the dialog will not close on ESC key nor on click on backdrop
   * @default false
   */
  strict?: boolean;

  /** Setting false hides close button and enforce strict mode
   * @default true
   */
  closable?: boolean;
}

export interface OptionalProps {
  /** onClick function to toggle visibility */
  onClick?: () => void;
  /** Custom class name for the modal */
  className?: string;
  /** Custom class name for content inside the modal  */
  contentClassName?: string;
  /** Optional panel that is docked under the content area */
  bottomPanel?: React.ReactNode;
  /** Class name for bottom panel */
  bottomPanelClassName?: string;
  /** Custom class name for dialog */
  dialogClassName?: string;
}

export type ComponentProps = Props &
  DefaultProps &
  OptionalProps &
  TrackEventProps;

export const Modal: React.FC<React.PropsWithChildren<ComponentProps>> = ({
  enableTracking = true,
  ariaLabelCloseButton,
  ariaLabel,
  closable = true,
  visible,
  tracking = {},
  children,
  contentClassName,
  className,
  onClick,
  bottomPanel,
  bottomPanelClassName,
  strict,
  dialogClassName
}) => {
  const { options: trackingOptions } = useContext(TrackingContext);

  const [scrollable, setScrollable] = useState<boolean>(false);
  const [scrolledToBottom, setScrolledToBottom] = useState<boolean>(false);

  const modalContentRef = useRef(null);
  const strictMode = strict || !closable;

  const previousElement = document.activeElement;

  const track = (eventAction: 'impression' | 'close') => {
    if (enableTracking) {
      trackEvent(
        {
          ...DEFAULT_TRACKING,
          ...tracking,
          eventAction,
          eventLabel: tracking.eventLabel || ariaLabel || 'modal'
        },
        trackingOptions
      );
    }
  };

  const onScroll = () => {
    if (modalContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = modalContentRef.current;
      /** Set 5px margin due to marginal calculation differences in browsers */
      if (Math.abs(scrollTop + clientHeight - scrollHeight) < 5) {
        setScrolledToBottom(true);
        return true;
      }
    }
    setScrolledToBottom(false);
    return false;
  };

  const lockBodyScroll = (visible): (() => void) => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    }
    return () => (document.body.style.overflow = 'visible');
  };

  useEffect(() => {
    console.warn(
      `@gjensidige/core-modal will be deprecated. Please See https://www.gjensidige.builders/docs/core/?path=/docs/nci-breaking-changes-core-30--page`
    );
  }, []);

  useEffect(() => {
    if (visible) {
      track('impression');
      const focusableModalElements = ref.current.querySelectorAll(
        'div, a[href], button:not([disabled]), textarea, input, select'
      );

      const firstElement = focusableModalElements[0];
      firstElement.focus();
    }
    if (modalContentRef.current) {
      const { scrollHeight, clientHeight } = modalContentRef.current;
      if (scrollHeight > clientHeight) {
        setScrollable(true);
      }
    }
    return lockBodyScroll(visible);
  }, [visible]);

  useEscape(() => {
    if (!strictMode) {
      handleClose();
      track('close');
    }
  }, visible);

  const handleClose = () => {
    onClick();
    (previousElement as HTMLElement).focus();
  };

  const ref = useOnTouchOutside(visible && !strictMode, () => handleClose());

  const keyDownHandler = (e: KeyboardEvent<HTMLImageElement>) => {
    if (e.key !== 'Tab') return;

    const focusableModalElements = ref.current.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select'
    );

    const firstElement = focusableModalElements[0];
    const lastElement =
      focusableModalElements[focusableModalElements.length - 1];

    if (!e.shiftKey && document.activeElement === lastElement) {
      firstElement.focus();
      return e.preventDefault();
    }

    if (e.shiftKey && document.activeElement === firstElement) {
      lastElement.focus();
      e.preventDefault();
    }
  };

  return (
    <div
      data-testid="modal"
      className={classNames(styles.modal, {
        [styles.visible]: visible
      })}
    >
      {visible && (
        <div
          ref={ref}
          className={classNames(styles.dialog, {
            [dialogClassName]: !!dialogClassName
          })}
        >
          <div
            onKeyDown={keyDownHandler}
            role="dialog"
            id="dialog"
            aria-label={ariaLabel}
            aria-modal="true"
            tabIndex={0}
            className={classNames(
              styles['py-2'],
              styles['py-md-3'],
              { [className]: !!className },
              {}
            )}
          >
            {closable && (
              <button
                aria-label={ariaLabelCloseButton}
                className={styles.button}
                // eslint-disable-next-line
                // @ts-ignore: this
                htmlFor="close" // internal API for using button
                type="button"
                data-testid="modal-close-button"
                onClick={() => {
                  handleClose();
                  track('close');
                }}
              >
                <Close />
              </button>
            )}
            <div
              onScroll={onScroll}
              ref={modalContentRef}
              className={classNames(
                styles.content,
                styles['px-2'],
                styles['px-md-3'],
                {
                  [contentClassName]: !!contentClassName,
                  [styles.scrollbar]: scrollable
                }
              )}
            >
              {children}
            </div>
            {bottomPanel && (
              <div
                className={classNames(
                  styles.bottomPanel,
                  styles['pt-2'],
                  styles['px-2'],
                  styles['pt-md-3'],
                  styles['px-md-3'],
                  {
                    [bottomPanelClassName]: !!bottomPanelClassName,
                    [styles.scrollShadow]: scrollable && !scrolledToBottom
                  }
                )}
              >
                {bottomPanel}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
